import type { DealAnalyzerAnalysis } from '../types/dealAnalyzer';

export const SAVED_DEAL_ANALYSES_STORAGE_KEY = 'bcc.savedDealAnalyses.v1';
export const SAVED_DEAL_ANALYSES_CHANGED = 'bcc:saved-deal-analyses-changed';

/** Soft cap for UI / browser quota messaging (approximate JSON size). */
export const SAVED_DEAL_ANALYSES_QUOTA_BYTES = Math.floor(4.5 * 1024 * 1024);

export type SavedDealAnalysisRecord = {
  id: string;
  savedAt: string;
  label: string;
  analysis: DealAnalyzerAnalysis;
};

function parseList(raw: string | null): SavedDealAnalysisRecord[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw) as unknown;
    if (!Array.isArray(v)) return [];
    return v.filter(
      (x): x is SavedDealAnalysisRecord =>
        typeof x === 'object' &&
        x !== null &&
        typeof (x as SavedDealAnalysisRecord).id === 'string' &&
        typeof (x as SavedDealAnalysisRecord).savedAt === 'string' &&
        typeof (x as SavedDealAnalysisRecord).label === 'string' &&
        typeof (x as SavedDealAnalysisRecord).analysis === 'object'
    );
  } catch {
    return [];
  }
}

export function listSavedDealAnalyses(): SavedDealAnalysisRecord[] {
  try {
    const list = parseList(localStorage.getItem(SAVED_DEAL_ANALYSES_STORAGE_KEY));
    return list.sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1));
  } catch {
    return [];
  }
}

export function getSavedDealAnalysis(id: string): SavedDealAnalysisRecord | undefined {
  return listSavedDealAnalyses().find((r) => r.id === id);
}

export function getSavedDealAnalysesStorageBytes(): number {
  try {
    const raw = localStorage.getItem(SAVED_DEAL_ANALYSES_STORAGE_KEY) ?? '[]';
    return new TextEncoder().encode(raw).length;
  } catch {
    return 0;
  }
}

function notifySavedChanged(): void {
  window.dispatchEvent(new Event(SAVED_DEAL_ANALYSES_CHANGED));
}

export function saveDealAnalysisRecord(
  label: string,
  analysis: DealAnalyzerAnalysis
): SavedDealAnalysisRecord {
  const snapshot: DealAnalyzerAnalysis = JSON.parse(JSON.stringify(analysis)) as DealAnalyzerAnalysis;
  const record: SavedDealAnalysisRecord = {
    id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    savedAt: new Date().toISOString(),
    label: label.trim() || 'Property analysis',
    analysis: snapshot,
  };

  const next = [record, ...listSavedDealAnalyses()];
  const json = JSON.stringify(next);
  const bytes = new TextEncoder().encode(json).length;
  if (bytes > SAVED_DEAL_ANALYSES_QUOTA_BYTES) {
    throw new Error(
      'Saved analyses would exceed storage for this device. Delete an older analysis and try again.'
    );
  }
  try {
    localStorage.setItem(SAVED_DEAL_ANALYSES_STORAGE_KEY, json);
  } catch {
    throw new Error('Could not save — browser storage may be full or unavailable.');
  }
  notifySavedChanged();
  return record;
}

export function deleteSavedDealAnalysis(id: string): void {
  const next = listSavedDealAnalyses().filter((r) => r.id !== id);
  localStorage.setItem(SAVED_DEAL_ANALYSES_STORAGE_KEY, JSON.stringify(next));
  notifySavedChanged();
}
